import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, type ReactElement } from 'react'

import { type PolymorphicElementProps, type TextTag } from '../types'

import styles from './text.module.css'

export type TextVariant =
  | 'labelSmall'
  | 'labelMedium'
  | 'bodySmall'
  | 'bodyMedium'
  | 'bodyLarge'
  | 'bodyXlarge'
  | 'headerSmall'
  | 'headerMedium'
  | 'headerLarge'
  | 'headerXlarge'
  | 'jumbo1'
  | 'jumbo2'

export type TextProps = ComponentPropsWithoutRef<TextTag> &
  PolymorphicElementProps<TextTag> & {
    variant?: TextVariant
    fontWeight?: 'regular' | 'medium' | 'semibold' | 'bold'
  }

const variantToClassNameMap: Record<NonNullable<TextProps['variant']>, string> = {
  labelSmall: styles.labelSmall,
  labelMedium: styles.labelMedium,
  bodySmall: styles.bodySmall,
  bodyMedium: styles.bodyMedium,
  bodyLarge: styles.bodyLarge,
  bodyXlarge: styles.bodyXlarge,
  headerSmall: styles.headerSmall,
  headerMedium: styles.headerMedium,
  headerLarge: styles.headerLarge,
  headerXlarge: styles.headerXlarge,
  jumbo1: styles.jumbo1,
  jumbo2: styles.jumbo2,
}

const fontWeightToClassNameMap: Record<NonNullable<TextProps['fontWeight']>, string> = {
  regular: styles.fontWeightRegular,
  medium: styles.fontWeightMedium,
  semibold: styles.fontWeightSemibold,
  bold: styles.fontWeightBold,
}

export function Text({
  as = 'p',
  children,
  variant,
  fontWeight,
  className,
  ...rest
}: TextProps): ReactElement {
  const Component = as

  return (
    <Component
      className={clsx(
        styles.text,
        variant && variantToClassNameMap[variant],
        fontWeight && fontWeightToClassNameMap[fontWeight],
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  )
}
